/**
 * TanStack Router instance + route tree (code-based for v1).
 *
 * Routes:
 *   /login                              → LoginPage
 *   /signup                             → SignupPage
 *   /forgot-password                    → ForgotPasswordPage (email a reset link)
 *   /reset-password                     → ResetPasswordPage (?token=… from the email)
 *   /confirm-email                      → ConfirmEmailPage (?token=… from the email)
 *   /auth/google/callback               → GoogleCallbackPage (auth service OAuth landing)
 *   /invite/:token                      → InviteAccept
 *   /                                   → LandingPage (signed-out visitors only)
 *   /:wsId                              → WorkspaceShell (sidebar + main)
 *   /:wsId/p/:pKey                      → ProjectBoard
 *   /:wsId/p/:pKey/t/:tKey              → ProjectBoard (drawer opens on tKey)
 *   /:wsId/p/:pKey/sprint/:sprintKey    → ProjectBoard (sprint scope)
 *   /:wsId/settings                     → WorkspaceSettingsView (General tab)
 *   /:wsId/settings/members             → Workspace settings tabs (FRIDAY-65)
 *   /:wsId/settings/labels
 *   /:wsId/settings/milestones
 *   /:wsId/settings/boards
 *   /:wsId/settings/pages
 *   /:wsId/p/:pKey/settings             → Board settings tabs (FRIDAY-65)
 *   /settings                           → SettingsPage
 *   /settings/tokens                    → ApiTokensPage
 *   /settings/github                    → GithubSettingsPage
 *
 * Phase 0d: skeleton. Phase 2 fills in the views.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { LoginPage } from '@/routes/login'
import { LandingPage } from '@/routes/landing'
import { SignupPage } from '@/routes/signup'
import { ForgotPasswordPage } from '@/routes/forgot-password'
import { ResetPasswordPage } from '@/routes/reset-password'
import { ConfirmEmailPage } from '@/routes/confirm-email'
import { GoogleCallbackPage } from '@/routes/auth-google-callback'
import { InviteAccept } from '@/routes/invite'
import { WorkspaceShell } from '@/routes/workspace'
import { ProjectBoard } from '@/routes/project'
import { MyWorkPage } from '@/routes/my-work'
import { ArchivePage } from '@/routes/archive'
import { SettingsPage } from '@/routes/settings'
import { ApiTokensPage } from '@/routes/tokens'
import { GithubSettingsPage } from '@/routes/github-settings'
import { MilestonesView } from '@/routes/milestones'
import { ProjectMembersView } from '@/routes/project-members'
import { WorkspaceMembersView } from '@/routes/workspace-members'
import { WorkspaceOverview } from '@/routes/workspace-overview'
import {
  WorkspaceSettingsBoardsView,
  WorkspaceSettingsPagesView,
  WorkspaceSettingsLabelsView,
  WorkspaceSettingsLayout,
  WorkspaceSettingsMembersView,
  WorkspaceSettingsMilestonesView,
  WorkspaceSettingsView,
} from '@/routes/workspace-settings'
import {
  BoardSettingsLayout,
  BoardSettingsMembersView,
  BoardSettingsLabelsView,
  BoardSettingsMilestonesView,
  BoardSettingsView,
} from '@/routes/board-settings'
import { NotebookPage } from '@/routes/notebook'
import { useAuth } from '@/features/auth/authContext'

function NotFound() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
      <h1 className="text-2xl font-semibold">404</h1>
      <p className="text-surface-500">This page wandered off.</p>
    </div>
  )
}

function RootLayout({ children }: { children: ReactNode }) {
  return <div className="h-full">{children}</div>
}

const rootRoute = createRootRoute({
  component: () => (
    <RootLayout>
      <Outlet />
    </RootLayout>
  ),
  notFoundComponent: NotFound,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // The marketing page is for signed-out visitors; it bounces a logged-in
  // visitor to their workspace once the session resolves.
  component: LandingPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s) => ({ next: typeof s.next === 'string' ? s.next : undefined }),
  component: LoginPage,
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: (s) => ({ next: typeof s.next === 'string' ? s.next : undefined }),
  component: SignupPage,
})

/** Landing page of the auth service's Google sign-in flow. */
const googleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/google/callback',
  validateSearch: (s) => ({
    code: typeof s.code === 'string' ? s.code : undefined,
    state: typeof s.state === 'string' ? s.state : undefined,
    error: typeof s.error === 'string' ? s.error : undefined,
  }),
  component: GoogleCallbackPage,
})

/** Asks the auth service to email a password reset link. */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

/** Sets a new password from the token in the reset email. */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (s) => ({ token: typeof s.token === 'string' ? s.token : undefined }),
  component: ResetPasswordPage,
})

/** Redeems the email confirmation token from the confirmation email. */
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-email',
  validateSearch: (s) => ({ token: typeof s.token === 'string' ? s.token : undefined }),
  component: ConfirmEmailPage,
})

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InviteAccept,
})

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w',
  component: WorkspaceShell,
})

const myWorkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: () => (
    <WorkspaceShell>
      <MyWorkPage />
    </WorkspaceShell>
  ),
})

const archiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me/archive',
  component: () => (
    <WorkspaceShell>
      <ArchivePage />
    </WorkspaceShell>
  ),
})

const workspaceWsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$wsId',
  component: () => (
    <WorkspaceShell>
      <Outlet />
    </WorkspaceShell>
  ),
})

const workspaceOverviewRoute = createRoute({
  getParentRoute: () => workspaceWsRoute,
  path: '/',
  component: WorkspaceOverview,
})

const projectRoute = createRoute({
  getParentRoute: () => workspaceWsRoute,
  path: 'p/$pKey',
  component: ProjectBoard,
})

const taskRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 't/$tKey',
  component: ProjectBoard,
})

const sprintRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'sprint/$sprintKey',
  component: ProjectBoard,
})

const milestonesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'milestones',
  component: MilestonesView,
})

const membersRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'members',
  component: ProjectMembersView,
})

const boardSettingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'settings',
  component: BoardSettingsLayout,
})

// The board settings tabs are routes, so the URL is the source of truth for
// the selected tab (FRIDAY-65).
const boardSettingsBoardRoute = createRoute({
  getParentRoute: () => boardSettingsRoute,
  path: '/',
  component: BoardSettingsView,
})

const boardSettingsMembersRoute = createRoute({
  getParentRoute: () => boardSettingsRoute,
  path: 'members',
  component: BoardSettingsMembersView,
})

const boardSettingsLabelsRoute = createRoute({
  getParentRoute: () => boardSettingsRoute,
  path: 'labels',
  component: BoardSettingsLabelsView,
})

const boardSettingsMilestonesRoute = createRoute({
  getParentRoute: () => boardSettingsRoute,
  path: 'milestones',
  component: BoardSettingsMilestonesView,
})

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => workspaceWsRoute,
  path: 'settings',
  component: WorkspaceSettingsLayout,
})

// The settings tabs are routes, so the URL is the source of truth for the
// selected tab (FRIDAY-65). `/settings` stays the General tab.
const workspaceSettingsGeneralRoute = createRoute({
  getParentRoute: () => workspaceSettingsRoute,
  path: '/',
  component: WorkspaceSettingsView,
})

const workspaceSettingsMembersRoute = createRoute({
  getParentRoute: () => workspaceSettingsRoute,
  path: 'members',
  component: WorkspaceSettingsMembersView,
})

const workspaceSettingsLabelsRoute = createRoute({
  getParentRoute: () => workspaceSettingsRoute,
  path: 'labels',
  component: WorkspaceSettingsLabelsView,
})

const workspaceSettingsMilestonesRoute = createRoute({
  getParentRoute: () => workspaceSettingsRoute,
  path: 'milestones',
  component: WorkspaceSettingsMilestonesView,
})

const workspaceSettingsBoardsRoute = createRoute({
  getParentRoute: () => workspaceSettingsRoute,
  path: 'boards',
  component: WorkspaceSettingsBoardsView,
})

const workspaceSettingsPagesRoute = createRoute({
  getParentRoute: () => workspaceSettingsRoute,
  path: 'pages',
  component: WorkspaceSettingsPagesView,
})

const workspaceMembersRoute = createRoute({
  getParentRoute: () => workspaceWsRoute,
  path: 'share',
  component: WorkspaceMembersView,
})

const notebookIndexRoute = createRoute({
  getParentRoute: () => workspaceWsRoute,
  path: 'n',
  component: NotebookPage,
})

const notebookPageRoute = createRoute({
  getParentRoute: () => workspaceWsRoute,
  path: 'n/$pageId',
  component: NotebookPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: () => (
    <WorkspaceShell>
      <SettingsPage />
    </WorkspaceShell>
  ),
})

const tokensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/tokens',
  component: () => (
    <WorkspaceShell>
      <ApiTokensPage />
    </WorkspaceShell>
  ),
})

const githubSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/github',
  component: () => (
    <WorkspaceShell>
      <GithubSettingsPage />
    </WorkspaceShell>
  ),
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  confirmEmailRoute,
  googleCallbackRoute,
  inviteRoute,
  workspaceRoute,
  workspaceWsRoute.addChildren([
    workspaceOverviewRoute,
    workspaceMembersRoute,
    workspaceSettingsRoute.addChildren([
      workspaceSettingsGeneralRoute,
      workspaceSettingsMembersRoute,
      workspaceSettingsLabelsRoute,
      workspaceSettingsMilestonesRoute,
      workspaceSettingsBoardsRoute,
      workspaceSettingsPagesRoute,
    ]),
    notebookIndexRoute,
    notebookPageRoute,
    projectRoute.addChildren([
      taskRoute,
      sprintRoute,
      milestonesRoute,
      membersRoute,
      boardSettingsRoute.addChildren([
        boardSettingsBoardRoute,
        boardSettingsMembersRoute,
        boardSettingsLabelsRoute,
        boardSettingsMilestonesRoute,
      ]),
    ]),
  ]),
  myWorkRoute,
  archiveRoute,
  settingsRoute,
  tokensRoute,
  githubSettingsRoute,
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
